import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, Loader2 } from "lucide-react";

interface SlideTheme {
  backgroundColor: string;
  titleColor: string;
  textColor: string;
  accentColor: string;
  fontFamily: string;
  titleFontSize: number;
  textFontSize: number;
  bulletStyle: string;
  backgroundGradient?: string | null;
  description?: string;
}

interface ThemeDesignerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentTheme?: SlideTheme;
  onApplyTheme: (theme: SlideTheme) => void;
}

const ThemeDesignerDialog = ({
  open,
  onOpenChange,
  currentTheme,
  onApplyTheme,
}: ThemeDesignerDialogProps) => {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const examplePrompts = [
    "Modern minimalist with dark blue and white",
    "Corporate professional with navy and gold accents",
    "Creative and colorful with gradients",
    "Nature-inspired with green tones",
    "Tech startup with purple and neon accents"
  ];

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast({
        variant: "destructive",
        title: "Prompt required",
        description: "Please describe the theme you want",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-slide-theme', {
        body: {
          prompt,
          currentTheme
        }
      });

      if (error) throw error;

      if (data?.theme) {
        onApplyTheme(data.theme);
        toast({
          title: "Theme applied!",
          description: data.theme.description || "Your slides have been updated with the new theme",
        });
        onOpenChange(false);
        setPrompt("");
      }
    } catch (error) {
      console.error('Theme generation error:', error);
      toast({
        variant: "destructive",
        title: "Error generating theme",
        description: error instanceof Error ? error.message : "Failed to generate theme",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Theme Designer
          </DialogTitle>
          <DialogDescription>
            Describe your ideal slide design and AI will create a matching theme
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="theme-prompt">Theme Description</Label>
            <Input
              id="theme-prompt"
              placeholder="e.g., Professional corporate with blue and white colors"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !loading) {
                  handleGenerate();
                }
              }}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Try these examples:</Label>
            <div className="flex flex-wrap gap-2">
              {examplePrompts.map((example, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  onClick={() => setPrompt(example)}
                  className="text-xs"
                >
                  {example}
                </Button>
              ))}
            </div>
          </div>

          {currentTheme && (
            <div className="rounded-lg border p-3 space-y-2">
              <Label className="text-sm font-medium">Current Theme Preview</Label>
              <div className="grid grid-cols-4 gap-2">
                <div className="space-y-1">
                  <div 
                    className="h-8 rounded border"
                    style={{ backgroundColor: currentTheme.backgroundColor }}
                  />
                  <p className="text-xs text-center text-muted-foreground">Background</p>
                </div>
                <div className="space-y-1">
                  <div 
                    className="h-8 rounded border"
                    style={{ backgroundColor: currentTheme.titleColor }}
                  />
                  <p className="text-xs text-center text-muted-foreground">Title</p>
                </div>
                <div className="space-y-1">
                  <div 
                    className="h-8 rounded border"
                    style={{ backgroundColor: currentTheme.textColor }}
                  />
                  <p className="text-xs text-center text-muted-foreground">Text</p>
                </div>
                <div className="space-y-1">
                  <div 
                    className="h-8 rounded border"
                    style={{ backgroundColor: currentTheme.accentColor }}
                  />
                  <p className="text-xs text-center text-muted-foreground">Accent</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleGenerate} disabled={loading || !prompt.trim()}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Theme
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ThemeDesignerDialog;
